import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Memo Management - Admin Dashboard
 *
 * View, manage, and track all memo requests from dealers
 * Admin-focused interface for the Financial Hub
 */
import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import mockDataService from '../../services/mockDataService';
import { MemoStatus, MEMO_STATUS_LABELS, MEMO_STATUS_COLORS } from '../../types/memo';
import { useAuthStore } from '../../stores/authStore';
import { FaClipboardList, FaCheck, FaTimes, FaTruck, FaUndo, FaSync, FaClock, FaShippingFast, FaDollarSign, FaPlus } from 'react-icons/fa';
import { CreateMemoModal } from '../../components/CreateMemoModal';
import TabPageLayout from './FinancialHub/TabPageLayout';
import { Button, StatsGrid, StatCard, StatIcon, StatLabel, StatValue, TableContainer, Table, Th, Td, Tr, FiltersContainer, FilterGroup, Label, Input, Select, EmptyState, EmptyStateIcon, EmptyStateText, SortableTh } from './FinancialHub/SharedComponents';
// Status Badge
const StatusBadge = styled.span `
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => MEMO_STATUS_COLORS[props.status]}15;
  color: ${props => MEMO_STATUS_COLORS[props.status]};
  border: 1px solid ${props => MEMO_STATUS_COLORS[props.status]}40;
`;
// Action buttons
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => {
    switch (props.variant) {
        case 'primary':
            return `
          background: rgba(184, 134, 11, 0.1);
          border-color: #B8860B;
          color: #B8860B;
          &:hover { background: rgba(184, 134, 11, 0.2); }
        `;
        case 'success':
            return `
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
          color: #10b981;
          &:hover { background: rgba(16, 185, 129, 0.2); }
        `;
        case 'danger':
            return `
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
          &:hover { background: rgba(239, 68, 68, 0.2); }
        `;
        default:
            return `
          background: var(--bg-tertiary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover { background: var(--bg-secondary); }
        `;
    }
}}
`;
const DiamondInfo = styled.div `
  .diamond-title {
    font-weight: 600;
    color: var(--text-primary);
  }
  .diamond-cert {
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-top: 2px;
  }
`;
const DealerInfo = styled.div `
  .dealer-name {
    font-weight: 500;
    color: var(--text-primary);
  }
  .dealer-business {
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
`;
const MemoManagement = () => {
    const authStore = useAuthStore();
    const [memos, setMemos] = useState([]);
    const [filteredMemos, setFilteredMemos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const isFetchingRef = useRef(false);
    // Sort state
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const fetchMemos = async () => {
        if (isFetchingRef.current)
            return;
        isFetchingRef.current = true;
        try {
            setIsLoading(true);
            if (mockDataService.isMockMode()) {
                console.log('MemoManagement - Using mock data');
                const mockMemos = mockDataService.getMemos();
                setMemos(mockMemos);
                setFilteredMemos(mockMemos);
            }
            else {
                const response = await apiClient.get('memos/all-v2');
                if (response.ok && response.data?.success && response.data.memos) {
                    setMemos(response.data.memos);
                    setFilteredMemos(response.data.memos);
                }
                else {
                    console.error('Failed to load memos:', response.error);
                    notify.error('Error', response.error || 'Failed to load memos');
                    setMemos([]);
                    setFilteredMemos([]);
                }
            }
        }
        catch (error) {
            console.error('Unexpected error:', error);
            notify.error('Error', 'Failed to load memos');
            setMemos([]);
            setFilteredMemos([]);
        }
        finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };
    useEffect(() => {
        if (authStore.isAuthenticated && !authStore.isLoading) {
            fetchMemos();
        }
    }, [authStore.isAuthenticated, authStore.isLoading]);
    // Handle sort
    const handleSort = (key) => {
        if (sortKey === key) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortKey(null);
            }
            else {
                setSortDirection('asc');
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    // Sort memos
    const sortMemos = (memosToSort) => {
        if (!sortKey || !sortDirection)
            return memosToSort;
        return [...memosToSort].sort((a, b) => {
            let aValue;
            let bValue;
            switch (sortKey) {
                case 'diamond':
                    aValue = `${a.diamondDetails.caratWeight} ${a.diamondDetails.shape}`.toLowerCase();
                    bValue = `${b.diamondDetails.caratWeight} ${b.diamondDetails.shape}`.toLowerCase();
                    break;
                case 'dealer':
                    aValue = a.dealerName?.toLowerCase() || '';
                    bValue = b.dealerName?.toLowerCase() || '';
                    break;
                case 'client':
                    aValue = a.clientName?.toLowerCase() || '';
                    bValue = b.clientName?.toLowerCase() || '';
                    break;
                case 'price':
                    aValue = a.diamondDetails.priceUSD || 0;
                    bValue = b.diamondDetails.priceUSD || 0;
                    break;
                case 'requestedAt':
                    aValue = new Date(a.requestedAt).getTime();
                    bValue = new Date(b.requestedAt).getTime();
                    break;
                case 'dueDate':
                    aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                    bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                default:
                    return 0;
            }
            if (aValue < bValue)
                return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue)
                return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };
    // Filter and sort memos
    useEffect(() => {
        let filtered = memos;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(memo => memo.dealerName.toLowerCase().includes(term) ||
                memo.dealerEmail.toLowerCase().includes(term) ||
                memo.diamondDetails.stockNumber?.toLowerCase().includes(term) ||
                memo.diamondDetails.certificateNumber?.toLowerCase().includes(term));
        }
        if (statusFilter !== 'all') {
            filtered = filtered.filter(memo => memo.status === statusFilter);
        }
        // Apply sorting
        filtered = sortMemos(filtered);
        setFilteredMemos(filtered);
    }, [memos, searchTerm, statusFilter, sortKey, sortDirection]);
    const handleStatusChange = async (memoId, newStatus) => {
        try {
            if (mockDataService.isMockMode()) {
                const updated = mockDataService.updateMemo(memoId, { status: newStatus });
                if (updated) {
                    await fetchMemos();
                    notify.success('Status Updated', `Memo status changed to ${MEMO_STATUS_LABELS[newStatus]}`);
                }
            }
            else {
                const response = await apiClient.put(`memos/update-status/${memoId}`, { status: newStatus });
                if (response.ok && response.data?.success) {
                    await fetchMemos();
                    notify.success('Status Updated', `Memo status changed to ${MEMO_STATUS_LABELS[newStatus]}`);
                }
                else {
                    notify.error('Update Failed', response.error || 'Failed to update memo status');
                }
            }
        }
        catch (error) {
            console.error('Status update failed:', error);
            notify.error('Update Failed', 'Failed to update memo status');
        }
    };
    const stats = {
        total: memos.length,
        pending: memos.filter(m => m.status === MemoStatus.PENDING).length,
        approved: memos.filter(m => m.status === MemoStatus.APPROVED).length,
        shipped: memos.filter(m => m.status === MemoStatus.SHIPPED || m.status === MemoStatus.WITH_CLIENT).length,
        totalValue: memos.reduce((sum, m) => sum + (m.diamondDetails.priceUSD || 0), 0),
    };
    // Stats section
    const renderStats = () => (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { color: "#3b82f6", children: [_jsx(StatIcon, { color: "#3b82f6", children: _jsx(FaClipboardList, {}) }), _jsx(StatLabel, { children: "Total Memos" }), _jsx(StatValue, { children: stats.total })] }), _jsxs(StatCard, { color: "#f59e0b", children: [_jsx(StatIcon, { color: "#f59e0b", children: _jsx(FaClock, {}) }), _jsx(StatLabel, { children: "Pending Approval" }), _jsx(StatValue, { children: stats.pending })] }), _jsxs(StatCard, { color: "#10b981", children: [_jsx(StatIcon, { color: "#10b981", children: _jsx(FaShippingFast, {}) }), _jsx(StatLabel, { children: "Active (Shipped)" }), _jsx(StatValue, { children: stats.shipped })] }), _jsxs(StatCard, { color: "#8b5cf6", children: [_jsx(StatIcon, { color: "#8b5cf6", children: _jsx(FaDollarSign, {}) }), _jsx(StatLabel, { children: "Total Value" }), _jsxs(StatValue, { children: ["$", stats.totalValue.toLocaleString()] })] })] }));
    // Actions section
    const renderActions = () => (_jsxs("div", { style: { display: 'flex', gap: '0.75rem' }, children: [_jsxs(Button, { "$primary": true, onClick: () => setShowCreateModal(true), children: [_jsx(FaPlus, { style: { marginRight: '0.5rem' } }), " Create Memo"] }), _jsxs(Button, { onClick: fetchMemos, disabled: isLoading, children: [_jsx(FaSync, { style: { marginRight: '0.5rem' } }), " ", isLoading ? 'Loading...' : 'Refresh'] })] }));
    // Filters section
    const renderFilters = () => (_jsxs(FiltersContainer, { children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Search" }), _jsx(Input, { type: "text", placeholder: "Search by dealer, stock #, cert #...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Statuses" }), Object.entries(MEMO_STATUS_LABELS).map(([status, label]) => (_jsx("option", { value: status, children: label }, status)))] })] })] }));
    // Table
    const renderTable = () => {
        if (filteredMemos.length === 0) {
            return (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, { children: _jsx(FaClipboardList, {}) }), _jsx(EmptyStateText, { children: searchTerm || statusFilter !== 'all'
                            ? 'No memos found matching your criteria'
                            : 'No memo requests yet' })] }));
        }
        return (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(SortableTh, { sortKey: "diamond", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Diamond" }), _jsx(SortableTh, { sortKey: "dealer", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Dealer" }), _jsx(SortableTh, { sortKey: "client", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Client" }), _jsx(SortableTh, { sortKey: "price", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Price" }), _jsx(SortableTh, { sortKey: "requestedAt", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Requested" }), _jsx(SortableTh, { sortKey: "dueDate", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Due Date" }), _jsx(SortableTh, { sortKey: "status", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: filteredMemos.map((memo) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs(DiamondInfo, { children: [_jsxs("div", { className: "diamond-title", children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape, " ", memo.diamondDetails.color, " ", memo.diamondDetails.clarity] }), _jsxs("div", { className: "diamond-cert", children: [memo.diamondDetails.stockNumber && `Stock: ${memo.diamondDetails.stockNumber}`, memo.diamondDetails.stockNumber && memo.diamondDetails.certificateNumber && ' • ', memo.diamondDetails.certificateNumber && `Cert: ${memo.diamondDetails.certificateNumber}`] })] }) }), _jsx(Td, { children: _jsxs(DealerInfo, { children: [_jsx("div", { className: "dealer-name", children: memo.dealerName }), _jsx("div", { className: "dealer-business", children: memo.dealerBusinessName || memo.dealerEmail })] }) }), _jsx(Td, { children: memo.clientName || '-' }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", memo.diamondDetails.priceUSD.toLocaleString()] }) }), _jsx(Td, { children: new Date(memo.requestedAt).toLocaleDateString() }), _jsx(Td, { children: memo.dueDate ? new Date(memo.dueDate).toLocaleDateString() : '-' }), _jsx(Td, { children: _jsx(StatusBadge, { status: memo.status, children: MEMO_STATUS_LABELS[memo.status] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [memo.status === MemoStatus.PENDING && (_jsxs(_Fragment, { children: [_jsx(ActionButton, { variant: "success", onClick: () => handleStatusChange(memo.id, MemoStatus.APPROVED), title: "Approve", children: _jsx(FaCheck, {}) }), _jsx(ActionButton, { variant: "danger", onClick: () => handleStatusChange(memo.id, MemoStatus.REJECTED), title: "Reject", children: _jsx(FaTimes, {}) })] })), memo.status === MemoStatus.APPROVED && (_jsxs(ActionButton, { variant: "primary", onClick: () => handleStatusChange(memo.id, MemoStatus.SHIPPED), title: "Mark Shipped", children: [_jsx(FaTruck, {}), " Ship"] })), (memo.status === MemoStatus.SHIPPED || memo.status === MemoStatus.WITH_CLIENT) && (_jsxs(ActionButton, { onClick: () => handleStatusChange(memo.id, MemoStatus.RETURNED), title: "Mark Returned", children: [_jsx(FaUndo, {}), " Return"] })), (memo.status === MemoStatus.REJECTED || memo.status === MemoStatus.RETURNED) && (_jsx("span", { style: { color: 'var(--text-tertiary)', fontSize: '0.75rem' }, children: "\u2014" }))] }) })] }, memo.id))) })] }) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(TabPageLayout, { title: "Memo Management", subtitle: "Track and manage all memo requests from dealers", stats: renderStats(), actions: renderActions(), filters: renderFilters(), children: isLoading ? (_jsx("div", { style: { padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }, children: "Loading memos..." })) : (renderTable()) }), _jsx(CreateMemoModal, { isOpen: showCreateModal, onClose: () => setShowCreateModal(false), onSuccess: () => {
                    setShowCreateModal(false);
                    fetchMemos();
                } })] }));
};
export default MemoManagement;
